import { DateTime, Interval } from "luxon";
import { DEFAULT_TIMEZONE, FILTERED_TENDERS, IS_VENDOR_OPEN } from "../../../../app-constants";
import { capitalizeWord, roundOffByBankApproach, toDisplayableTime } from "@/utils";
import { ALCOHOL_LIMIT_MESSAGE_PER_ORDER, ALCOHOL_RESTRICTION_MESSAGE, MODIFIERS_REQUIRED_MESSAGE } from "./index";
import { useOrderAheadMapper } from "@/store/modules/orderAhead/mapper";
const { mapToEstimatedOrderTime } = useOrderAheadMapper();

export function getActiveVenueSettings(state, getters, rootState, rootGetters) {
  return rootGetters["venueSettings/getVenueSettings"];
}

export function getVendorsList(state) {
  return Array.isArray(state.vendorsList) ? state.vendorsList.filter((vendor) => +vendor.status !== 0) : [];
}

export function getActiveVendor(state) {
  return state.activeVendor;
}

export function getActiveVendorId(state) {
  return state.activeVendorId;
}

export function getVendorByID(state) {
  return (vendorId) => {
    if (!vendorId || !state.vendorsList || !state.vendorsList.length) return {};
    return state.vendorsList.find((vendor) => parseInt(vendor.id, 10) === parseInt(vendorId, 10)) || {};
  };
}

export function isAlcoholEnabled(state, getters) {
  return (getters.getActiveVendor && getters.getActiveVendor.alcoholEnabled) || false;
}

export function getAlcoholOptions(state, getters) {
  return (getters.getActiveVendor && getters.getActiveVendor.alcohol) || {};
}

export function maxAlcoholPerOrder(state, getters) {
  return getters.getAlcoholOptions.isAlcoholLimitPerOrderEnabled ? getters.getAlcoholOptions.alcoholLimitPerOrder : Infinity;
}

export function getOrderAlcoholLimitMessage(state, getters) {
  return getters.getAlcoholOptions.alcoholPerOrderLimitReachedErrorMessage || ALCOHOL_LIMIT_MESSAGE_PER_ORDER;
}

export function getAlcoholRestrictionMessage(state, getters) {
  return getters.getAlcoholOptions.alcoholRestrictionErrorMessage || ALCOHOL_RESTRICTION_MESSAGE;
}

export function getModifiersRequiredMessage() {
  return MODIFIERS_REQUIRED_MESSAGE;
}

export function getSubvendorById(state) {
  return (subVendorId) => {
    return state.subVendorList[subVendorId];
  };
}

export function applyFee(state, getters) {
  return getters.getActiveVendor ? getters.getActiveVendor.fee === 1 : false;
}

export function feeRate(state, getters) {
  return parseFloat(getters.applyFee ? getters.getActiveVendor.convenienceFee : 0, 10);
}

export const getPickupEstimatedTimeRange = (state, getters, rootState, rootGetters) => (vendorId) => {
  const activeVendorId = vendorId || getters.getActiveVendor.id;
  const timeSlotsAvailable = getters.getValidPickupTimesByVendorId(activeVendorId);
  const { timezone } = rootGetters["venue/getVenueInfo"];

  return timeSlotsAvailable.length ? `ASAP (Ready ${mapToEstimatedOrderTime(timeSlotsAvailable[0], timezone)})` : "";
};

export const isPickupTimeSelectionEnableByVendorId = (state, getters) => (vendorId) => {
  return getters.getVendorsList.find((vendor) => String(vendor.id) === String(vendorId))?.pickupTimeSelectionEnabled || false;
};

export const getPickupTimesByVendorId = (state, getters, rootState, rootGetters) => (vendorId) => {
  const curVendor = getters.getVendorsList.find((vendor) => vendor.id === vendorId);
  const openHours = getters.getMappedOpenHoursOfVendor(curVendor?.openHours);
  const { timezone } = rootGetters["venue/getVenueInfo"];
  const deliveryGap = curVendor?.deliveryGap;

  const now = DateTime.now().setZone(timezone);
  const currentWeekDay = now.toFormat("EEEE");

  const pickupTimes = curVendor?.pickupTimes || [];

  return pickupTimes.filter((time) => {
    const pickupTime = DateTime.fromISO(time)?.setZone(timezone);
    // Check pickupTimes array by current day openHours.  We are currently receiving
    // the current day pickupTimes from the service.  Once we get an expanded list for
    // order ahead over multiple days, this logic will need to check against the appropriate
    // open hour day.  Long term, we are discussing this list to be filtered by vendor openhours
    // in the ordering API service.

    return (
      !openHours ||
      openHours[currentWeekDay]?.some((timePeriod) => {
        return Interval.fromDateTimes(
          DateTime.fromISO(timePeriod.startTime).setZone(timezone),
          DateTime.fromISO(timePeriod.endTime).setZone(timezone)
        ).contains(pickupTime);
      })
    );
  });
};
export const getValidPickupTimesByVendorId = (state, getters, rootState, rootGetters) => (vendorId) => {
  return getters.getPickupTimesByVendorId(vendorId);
};
export const getValidPickupTimesForTodayByVendorId = (state, getters, rootState, rootGetters) => (vendorId) => {
  const { timezone } = rootGetters["venue/getVenueInfo"];
  const tomorrow = DateTime.now().setZone(timezone).plus({ day: 1 }).startOf("day");

  return getters.getPickupTimesByVendorId(vendorId).filter((pickupTime) => {
    return DateTime.fromISO(pickupTime).setZone(timezone) < tomorrow;
  });
};

export function getVendorFee(state, getters) {
  return (subTotal) => {
    const activeVendor = getters.getActiveVendor;
    if (!activeVendor) return 0;
    return activeVendor.feeInPercent ? roundOffByBankApproach(subTotal * (getters.feeRate / 100)) : getters.feeRate;
  };
}

export function isOfferingsPageAvailable(state, getters, rootState, rootGetters) {
  return !rootGetters["venueSettings/topLevelCategoriesAreSuppressed"];
}

export function getMappedOpenHoursOfVendor(state, getters, rootState, rootGetters) {
  return (openHours) => {
    const mappedOpenHours = {};
    const vendorHoursList = openHours || [];

    const setDate = (datetime, timezone = DEFAULT_TIMEZONE) => {
      const time = toDisplayableTime(datetime);
      const todayDate = DateTime.now().toFormat("yyyy-MM-dd");
      return DateTime.fromFormat(`${todayDate} ${time} ${timezone}`, "yyyy-MM-dd h:mm:ss a z");
    };

    vendorHoursList.forEach(({ day, startTime, endTime }) => {
      const { timezone } = rootGetters["venue/getVenueInfo"];
      const dayOfWeek = capitalizeWord(day);
      const timePeriod = {
        startTime: setDate(startTime, timezone),
        endTime: setDate(endTime, timezone)
      };

      if (!mappedOpenHours[dayOfWeek]) mappedOpenHours[dayOfWeek] = [];
      mappedOpenHours[dayOfWeek].push(timePeriod);
    });

    return mappedOpenHours;
  };
}

// added vendor status to both of these getters.
export function isActiveVendorOpen(state, getters) {
  const activeVendor = getters.getVendorsList.find((vendor) => vendor.id === state.activeVendorId);
  return !!(activeVendor && activeVendor[IS_VENDOR_OPEN] && activeVendor?.status);
}

export function isVendorOpenByVendorId(state, getters) {
  return (vendorId) => {
    const vendor = getters.getVendorByID(vendorId);
    return !!(vendor && vendor[IS_VENDOR_OPEN] && vendor?.status);
  };
}

export function getTenders(state, getters) {
  // if vendor is not selected return empty array
  if (!getters.getActiveVendor) return [];

  return (
    getters.getActiveVendor.tenders.filter(
      (tender) =>
        !FILTERED_TENDERS.some(
          (filtered) =>
            // if subpayment is null it will just go by paymentType
            filtered.paymentType === tender.paymentType &&
            (!filtered.subpaymentType || filtered.subpaymentType === tender.subpaymentType)
        )
    ) || []
  );
}

export function tokenizedGatewayId(state, getters) {
  return getters.getActiveVendor?.tokenizedGatewayId || 0;
}

export function getDeliveryGap(state) {
  return state.activeVendor?.deliveryGap || 0;
}

export function isManualInputEnabled(state, getters) {
  return Boolean(getters.getActiveVendor?.manualScanCodeEntry);
}
